import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import tenant management components (lazy-loaded for performance)
const TenantsList = React.lazy(() => import("./TenantsList"));
const CreateTenant = React.lazy(() => import("./CreateTenant"));
const EditTenant = React.lazy(() => import("./EditTenant"));
const ModuleDeployment = React.lazy(() => import("./ModuleDeployment"));
const QuickDeploy = React.lazy(() => import("./QuickDeploy"));
const MassDeploy = React.lazy(() => import("./MassDeploy"));
const StorageManagement = React.lazy(() => import("./StorageManagement"));

const tenantManagementModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "tenant-management",
    name: "Tenant Management",
    description: "Manage tenant accounts, configurations, and module deployments",
    version: "1.0.0",
    enabled: true,
    category: "administration",
    order: 95, // Before admin module
    icon: "🏢",
    color: "#4299E1",

    // 🛣️ ROUTES
    routes: [
        {
            path: "/tenants",
            component: TenantsList,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/tenants/new",
            component: CreateTenant,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/tenants/:id/edit",
            component: EditTenant,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/tenants/deploy",
            component: ModuleDeployment,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/tenants/quick-deploy",
            component: QuickDeploy,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/tenants/mass-deploy",
            component: MassDeploy,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            path: "/tenants/storage",
            component: StorageManagement,
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        }
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "All Tenants",
            path: "/tenants",
            icon: "🏢",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            label: "Create Tenant",
            path: "/tenants/new",
            icon: "➕",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            label: "Quick Deploy",
            path: "/tenants/quick-deploy",
            icon: "⚡",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            label: "Mass Deploy",
            path: "/tenants/mass-deploy",
            icon: "🚀",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            label: "Module Deployment",
            path: "/tenants/deploy",
            icon: "📦",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        },
        {
            label: "Storage Management",
            path: "/tenants/storage",
            icon: "💾",
            permissions: ["ADMIN", "TENANT_MASTER_ADMIN"]
        }
    ],

    // 🔐 PERMISSIONS - Highly restrictive for tenant management
    permissions: {
        view: ["ADMIN", "TENANT_MASTER_ADMIN"],
        create: ["ADMIN", "TENANT_MASTER_ADMIN"],
        edit: ["ADMIN", "TENANT_MASTER_ADMIN"],
        delete: ["ADMIN", "TENANT_MASTER_ADMIN"]
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Create Tenant",
            path: "/tenants/new",
            icon: "🏢",
            description: "Add a new tenant to the system"
        },
        {
            label: "Quick Deploy",
            path: "/tenants/quick-deploy",
            icon: "⚡",
            description: "Deploy modules to a single tenant"
        },
        {
            label: "Mass Deploy",
            path: "/tenants/mass-deploy",
            icon: "🚀",
            description: "Deploy modules to multiple tenants"
        },
        {
            label: "Module Deployment",
            path: "/tenants/deploy",
            icon: "📦",
            description: "Manage all module deployments"
        },
        {
            label: "Storage Management",
            path: "/tenants/storage",
            icon: "💾",
            description: "View and manage IPFS storage across tenants"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.1.0",
            releaseDate: "2026-01-31",
            title: "IPFS Storage Management",
            features: [
                "✨ New Storage Management page for viewing all IPFS files",
                "✨ Storage breakdown by tenant and source (Media CDN vs Drone)",
                "✨ File preview for images and videos",
                "✨ Delete files (unpin from IPFS + database)",
                "✨ Bulk delete functionality",
                "✨ Filter by tenant, source, and file type"
            ],
            notes: "Master admin can now view and manage IPFS storage across all tenants."
        },
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready multi-tenant management",
                "✅ Quick Deploy with local storage persistence",
                "✅ Mass Deploy for multiple tenants",
                "✅ Comprehensive tenant configuration",
                "✅ Module deployment automation",
                "✅ Payment receiving details management",
                "✅ Streamlined tenant creation workflow"
            ],
            notes: "Official stable release with advanced deployment automation and tenant management."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-11-21",
            title: "Tenant Configuration Improvements",
            features: [
                "✨ Enhanced tenant page with improved UI (408 lines)",
                "✨ Better tenant record management (314 insertions)",
                "✨ Simplified tenant module configuration (34 deletions)",
                "✨ Improved form handling and validation"
            ],
            notes: "Major tenant configuration page improvements for better usability."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-11-04",
            title: "GitHub Repository Tracking",
            features: [
                "✨ Added GitHub repository badges (18 lines)",
                "✨ Visual indicators for repos without GitHub integration",
                "✨ Better deployment status tracking"
            ],
            notes: "GitHub integration status visibility for deployments."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-11-02",
            title: "Mass Deployment System",
            features: [
                "✅ Created MassDeploy component (855 lines)",
                "✨ Enhanced QuickDeploy with browser storage (684 lines refactored)",
                "✨ Updated TenantsList for mass operations (40 lines)",
                "✨ Local storage for deployment settings",
                "✨ Faster mass module deployment across multiple tenants",
                "✨ Support for updating multiple modules simultaneously"
            ],
            notes: "Revolutionary mass deployment system for multi-tenant module updates."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-10-27",
            title: "Payment Receiving Details",
            features: [
                "✨ Added payment receiving details to tenant model (654 lines)",
                "✨ Enhanced EditTenant with payment configuration (651 insertions)",
                "✨ Better financial management capabilities"
            ],
            notes: "Payment receiving and financial configuration features."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-10-08",
            title: "Simplified Tenant Form",
            features: [
                "🔨 Major simplification of CreateTenant (1,429 deletions, 178 insertions)",
                "✨ Streamlined tenant creation workflow",
                "✨ Reduced complexity and improved UX",
                "✨ Faster tenant onboarding process"
            ],
            notes: "Major form simplification for better tenant creation experience."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-09-30",
            title: "Initial Tenant Management System",
            features: [
                "✅ Created CreateTenant component (1,388 lines)",
                "✅ Created DeploymentManager component (455 lines)",
                "✅ Created EditTenant component (2,756 lines)",
                "✅ Created TenantDeploymentManager (1,178 lines)",
                "✅ Created TenantsList component (1,231 lines)",
                "✅ Separated from admin module for better organization",
                "✅ Module deployment functionality",
                "✅ Comprehensive tenant configuration",
                "✅ Quick Deploy implementation (488 lines)",
                "✅ ModuleDeployment refactoring"
            ],
            notes: "Initial implementation separated from admin module with 7,113 lines of tenant-specific code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default tenantManagementModuleConfig;